import "/node_modules/flag-icons/css/flag-icons.min.css";
import { Button } from "primereact/button";
import { MultiSelect } from "primereact/multiselect";
import { useEffect, useRef, useState } from "react";
import { HEADER_INDEX } from "../../constants/app-constants";
import useCurrency from "../../hook/useCurrency";
import { translate } from "../../localization/translateService";
import { getCourseCategories } from "../../services/lookupService";
import DropdownRangeSlider from "../common/dropdowns/DropdownRangeSlider";

const FEE_RANGE_BY_CURRENCY = {
  KRW: { min: 0, max: 1_400_000, step: 10_000 },
  USD: { min: 0, max: 1_000, step: 50 },
  VND: { min: 0, max: 25_000_000, step: 100_000 },
};

const FILTER_TRANSLATION_KEYS = {
  tuitionFeeFilterHeader: "COURSE_MANAGEMENT.COLUMN_LABELS.COURSE_PRICE",
  sortByHeader: "CLASS.LABELS.SORT_BY",
  topicFilterHeader: "COURSE_CATEGORIES.LABELS.CATEGORY",
  tuitionFeeFilterPlaceholder: "TRAINERS.FILTER.TUITION_FEE_PLACEHOLDER",
  tuitionFeeRangeHeader: "CLASS.LABELS.TUITION_PER_CLASS",
  tuitionFeeRangeMin: "CLASS.LABELS.FROM",
  tuitionFeeRangeMax: "CLASS.LABELS.TO",
  ratingFilterHeader: "CLASS.LABELS.RATING",
  statusFilterHeader: "COURSE_LMS.LABELS.STATUS_HEADER",
  statusUpcoming: "REAL_TIME_LECTURES.STATUS.UPCOMING",
  statusInProgress: "COURSE_MANAGEMENT.LABELS.IN_PROGRESS",
  statusEnded: "COURSE_MANAGEMENT.LABELS.ENDED",
  timeOfDayFilterHeader: "CLASS.LABELS.TIME_OF_DAY",
  timeOfDayMorning: "CLASS.LABELS.MORNING",
  timeOfDayAfternoon: "CLASS.LABELS.AFTERNOON",
  timeOfDayEvening: "CLASS.LABELS.EVENING",
  clearButtonLabel: "CLASS.LABELS.CLEAR_BUTTON",
};

export default function ClassFilter({
  translationData,
  sortOptions = [],
  selectedPrice,
  sortBy,
  selectedTopic,
  selectedRating,
  selectedStatus,
  selectedTimeOfDay,
  onFilterPriceChange,
  onSortOptionChange,
  onFilterTopicChange,
  onFilterRatingChange,
  onFilterStatusChange,
  onFilterTimeOfDayChange,
  onFilterReset,
}) {
  const tuitionFeeFilterRef = useRef(null);
  const { defaultCurrency, ratesData, target: currentCurrency } = useCurrency();
  const [topicOptions, setTopicOptions] = useState([]);
  const [t, setT] = useState({});

  const { min: displayMin, max: displayMax, step: displayStep } = FEE_RANGE_BY_CURRENCY[currentCurrency] ?? { min: 0, max: 1000, step: 50 };

  useEffect(() => {
    const loadTopics = async () => {
      try {
        const data = await getCourseCategories();
        const opts = await Promise.all(
          (Array.isArray(data) ? data : []).map(async (o) => {
            const rawKey = o?.description ?? o?.value ?? o?.code ?? "";
            const translated =
              rawKey && typeof rawKey === "string"
                ? await translate(rawKey, true)
                : rawKey;
            return {
              name: translated || rawKey || "",
              value: o?.code ?? "",
            };
          })
        );
        setTopicOptions(opts.filter((o) => o.value));
      } catch {
        setTopicOptions([]);
      }
    };
    loadTopics();
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        const [
          tuitionFeeFilterHeader,
          sortByHeader,
          topicFilterHeader,
          tuitionFeeFilterPlaceholder,
          tuitionFeeRangeHeader,
          tuitionFeeRangeMin,
          tuitionFeeRangeMax,
          ratingFilterHeader,
          statusFilterHeader,
          statusUpcoming,
          statusInProgress,
          statusEnded,
          timeOfDayFilterHeader,
          timeOfDayMorning,
          timeOfDayAfternoon,
          timeOfDayEvening,
          clearButtonLabel,
        ] = await Promise.all([
          translate(FILTER_TRANSLATION_KEYS.tuitionFeeFilterHeader, true),
          translate(FILTER_TRANSLATION_KEYS.sortByHeader, true),
          translate(FILTER_TRANSLATION_KEYS.topicFilterHeader, true),
          translate(FILTER_TRANSLATION_KEYS.tuitionFeeFilterPlaceholder, true),
          translate(FILTER_TRANSLATION_KEYS.tuitionFeeRangeHeader, true),
          translate(FILTER_TRANSLATION_KEYS.tuitionFeeRangeMin, true),
          translate(FILTER_TRANSLATION_KEYS.tuitionFeeRangeMax, true),
          translate(FILTER_TRANSLATION_KEYS.ratingFilterHeader, true),
          translate(FILTER_TRANSLATION_KEYS.statusFilterHeader, true),
          translate(FILTER_TRANSLATION_KEYS.statusUpcoming, true),
          translate(FILTER_TRANSLATION_KEYS.statusInProgress, true),
          translate(FILTER_TRANSLATION_KEYS.statusEnded, true),
          translate(FILTER_TRANSLATION_KEYS.timeOfDayFilterHeader, true),
          translate(FILTER_TRANSLATION_KEYS.timeOfDayMorning, true),
          translate(FILTER_TRANSLATION_KEYS.timeOfDayAfternoon, true),
          translate(FILTER_TRANSLATION_KEYS.timeOfDayEvening, true),
          translate(FILTER_TRANSLATION_KEYS.clearButtonLabel, true),
        ]);
        setT({
          tuitionFeeFilterHeader,
          sortByHeader,
          topicFilterHeader,
          tuitionFeeFilterPlaceholder,
          tuitionFeeRangeHeader,
          tuitionFeeRangeMin,
          tuitionFeeRangeMax,
          ratingFilterHeader,
          statusFilterHeader,
          statusUpcoming,
          statusInProgress,
          statusEnded,
          timeOfDayFilterHeader,
          timeOfDayMorning,
          timeOfDayAfternoon,
          timeOfDayEvening,
          buttonLabel: { clearButtonLabel },
        });
      } catch {
        setT({});
      }
    };
    load();
  }, []);

  const tuitionFeeProps = {
    min: displayMin,
    max: displayMax,
    step: displayStep,
    placeholder: t.tuitionFeeFilterPlaceholder,
    rangeHeader: t.tuitionFeeRangeHeader,
    minHeader: t.tuitionFeeRangeMin,
    maxHeader: t.tuitionFeeRangeMax,
    currency: currentCurrency,
    ratesData,
    defaultCurrency,
  };

  const STATUS_OPTIONS = [
    { value: "upcoming", labelKey: "statusUpcoming" },
    { value: "in_progress", labelKey: "statusInProgress" },
    { value: "ended", labelKey: "statusEnded" },
  ];

  const statusProps = {
    options: STATUS_OPTIONS.map((o) => ({ value: o.value, label: t[o.labelKey] ?? o.value })),
  };

  const TIME_OF_DAY_OPTIONS = [
    { value: "morning", labelKey: "timeOfDayMorning" },
    { value: "afternoon", labelKey: "timeOfDayAfternoon" },
    { value: "evening", labelKey: "timeOfDayEvening" },
  ];

  const timeOfDayProps = {
    options: TIME_OF_DAY_OPTIONS.map((o) => ({ value: o.value, label: t[o.labelKey] ?? o.value })),
  };

  const RATING_OPTIONS = [
    { value: 5, stars: 5 },
    { value: 4, stars: 4 },
    { value: 3, stars: 3 },
    { value: 2, stars: 2 },
    { value: 1, stars: 1 },
  ];

  const onClear = () => {
    onFilterReset?.();
  };

  const clearButtonTemplate = (onClearClick, isShowNValueNumber = false, numberValue = 0) =>
    isShowNValueNumber ? (
      <div className="absolute z-5 flex flex-row keduall-form dropdown-input group-two-icon gap-3">
        <div className="flex keduall-typography text-16-normal-400 text-default-txt">({numberValue})</div>
        <Button
          icon="pi pi-times z-5 flex"
          className="p-button-text keduall-form dropdown-input icon w-max"
          onClick={(e) => {
            e.stopPropagation();
            onClearClick(null);
          }}
        />
      </div>
    ) : (
      <Button
        icon="pi pi-times z-5 flex"
        className="p-button-text absolute keduall-form dropdown-input icon icon-times-circle"
        onClick={(e) => {
          e.stopPropagation();
          onClearClick(null);
        }}
      />
    );

  const getTopicDisplay = (option) => {
    if (!option?.length) return null;
    const names = option.map((value) => {
      const topic = topicOptions.find((o) => o.value === value);
      return topic ? topic.name : value;
    });
    return names.join(", ");
  };

  const getStatusDisplay = (option) => {
    if (!option?.length) return null;
    const names = option.map((value) => {
      const statusOption = statusProps.options.find((o) => o.value === value);
      return statusOption ? statusOption.label : value;
    });
    return names.join(", ");
  };

  const getTimeOfDayDisplay = (option) => {
    if (!option?.length) return null;
    const names = option.map((value) => {
      const opt = timeOfDayProps.options.find((o) => o.value === value);
      return opt ? opt.label : value;
    });
    return names.join(", ");
  };

  const getRatingDisplay = (option) => {
    if (!option?.length) return null;
    const parts = option.map((value) => {
      const ratingOption = RATING_OPTIONS.find((o) => o.value === value);
      return ratingOption ? `${ratingOption.value}★` : value;
    });
    return parts.join(", ");
  };

  const ratingOptionTemplate = (option) => (
    <div className="flex align-items-center gap-1">
      {Array.from({ length: option.stars }).map((_, i) => (
        <i key={i} className="pi pi-star-fill class-filter-rating-star" />
      ))}
      <span className="keduall-typography text-16-normal-400 text-default-txt">({option.value})</span>
    </div>
  );

  const dropdownIconTemplate = (
    <i className="pi absolute cursor-pointer pi-angle-down keduall-form dropdown-input keduall-typography text-default icon-angle-down-small z-4 flex"></i>
  );

  const handleMultiSelectShow = (e) => {
    const panel = e?.target?.overlay || document.querySelector(".p-multiselect-panel");
    if (panel) panel.style.zIndex = `${HEADER_INDEX - 1}`;
  };
  const handleMultiSelectHide = (e) => {
    const panel = e?.target?.overlay || document.querySelector(".p-multiselect-panel");
    if (panel) panel.style.zIndex = "";
  };

  return (
    <div className="flex flex-column gap-2 w-full">
      <div className="flex flex-column gap-3 w-full">
        <div className="flex flex-column w-full">
          <div className="flex keduall-typography text-14-normal-400 text-default-txt mb-1">{t.topicFilterHeader ?? "Topic"}</div>
          <MultiSelect
            className="keduall-form form-without-search multi-select-input custom-multi-select custom-placeholder keduall-typography text-16-semibold-600 text-default-txt w-full"
            options={topicOptions}
            optionLabel="name"
            optionValue="value"
            placeholder=" "
            selectOnFocus={false}
            showSelectAll={false}
            appendTo="self"
            panelClassName="keduall-form dropdown-input-panel keduall-typography text-16-normal-400 text-default-txt w-webkit-fill-available"
            value={selectedTopic}
            showClear
            panelHeaderTemplate=""
            onChange={(e) => onFilterTopicChange?.(e.value)}
            clearIcon={clearButtonTemplate(() => onFilterTopicChange([]))}
            dropdownIcon={dropdownIconTemplate}
            tooltip={getTopicDisplay(selectedTopic)}
            tooltipOptions={{ className: "keduall-tooltip", position: "bottom" }}
            onShow={handleMultiSelectShow}
            onHide={handleMultiSelectHide}
          />
        </div>
        <div className="flex flex-column w-full">
          <div className="flex keduall-typography text-14-normal-400 text-default-txt mb-1">{t.statusFilterHeader ?? "Status"}</div>
          <MultiSelect
            className="keduall-form form-without-search multi-select-input custom-multi-select custom-placeholder keduall-typography text-16-semibold-600 text-default-txt w-full"
            options={statusProps.options}
            optionLabel="label"
            optionValue="value"
            placeholder=" "
            selectOnFocus={false}
            showSelectAll={false}
            appendTo="self"
            panelClassName="keduall-form dropdown-input-panel keduall-typography text-16-normal-400 text-default-txt w-webkit-fill-available"
            value={selectedStatus ?? []}
            showClear
            panelHeaderTemplate=""
            onChange={(e) => onFilterStatusChange?.(e.value ?? [])}
            clearIcon={clearButtonTemplate(() => onFilterStatusChange([]))}
            dropdownIcon={dropdownIconTemplate}
            tooltip={getStatusDisplay(selectedStatus)}
            tooltipOptions={{ className: "keduall-tooltip", position: "bottom" }}
            onShow={handleMultiSelectShow}
            onHide={handleMultiSelectHide}
          />
        </div>
        {/* <div className="flex flex-column w-full">
          <div className="flex keduall-typography text-14-normal-400 text-default-txt mb-1">{t.timeOfDayFilterHeader ?? "Time of day"}</div>
          <MultiSelect
            className="keduall-form form-without-search multi-select-input custom-multi-select custom-placeholder keduall-typography text-16-semibold-600 text-default-txt w-full"
            options={timeOfDayProps.options}
            optionLabel="label"
            optionValue="value"
            placeholder=" "
            selectOnFocus={false}
            showSelectAll={false}
            appendTo="self"
            panelClassName="keduall-form dropdown-input-panel keduall-typography text-16-normal-400 text-default-txt w-webkit-fill-available"
            value={selectedTimeOfDay ?? []}
            showClear
            panelHeaderTemplate=""
            onChange={(e) => onFilterTimeOfDayChange?.(e.value ?? [])}
            clearIcon={clearButtonTemplate(() => onFilterTimeOfDayChange([]))}
            dropdownIcon={dropdownIconTemplate}
            tooltip={getTimeOfDayDisplay(selectedTimeOfDay)}
            tooltipOptions={{ className: "keduall-tooltip", position: "bottom" }}
            onShow={handleMultiSelectShow}
            onHide={handleMultiSelectHide}
          />
        </div> */}
        <div className="flex flex-column w-full">
          <div className="flex keduall-typography text-14-normal-400 text-default-txt mb-1">{t.tuitionFeeFilterHeader}</div>
          <DropdownRangeSlider ref={tuitionFeeFilterRef} props={tuitionFeeProps} onChange={onFilterPriceChange} selectedPriceState={selectedPrice} />
        </div>
        <div className="flex flex-column w-full">
          <div className="flex keduall-typography text-14-normal-400 text-default-txt mb-1">{t.ratingFilterHeader ?? "Rating"}</div>
          <MultiSelect
            className="keduall-form form-without-search multi-select-input custom-multi-select custom-placeholder keduall-typography text-16-semibold-600 text-default-txt w-full"
            options={RATING_OPTIONS}
            optionLabel="value"
            optionValue="value"
            placeholder=" "
            selectOnFocus={false}
            showSelectAll={false}
            appendTo="self"
            panelClassName="keduall-form dropdown-input-panel keduall-typography text-16-normal-400 text-default-txt w-webkit-fill-available"
            value={selectedRating ?? []}
            showClear
            panelHeaderTemplate=""
            onChange={(e) => onFilterRatingChange?.(e.value ?? [])}
            clearIcon={clearButtonTemplate(() => onFilterRatingChange([]))}
            dropdownIcon={dropdownIconTemplate}
            itemTemplate={ratingOptionTemplate}
            tooltip={getRatingDisplay(selectedRating)}
            tooltipOptions={{ className: "keduall-tooltip", position: "bottom" }}
            onShow={handleMultiSelectShow}
            onHide={handleMultiSelectHide}
          />
        </div>
      </div>
      <div className="flex flex-row justify-content-end gap-2 flex-wrap md:flex-nowrap mt-2 p-2">
        <Button label={t.buttonLabel?.clearButtonLabel} className="keduall-buttons flex button-outlined-default-normal-secondary" onClick={onClear} />
      </div>
    </div>
  );
}
